import type { BodyFormat, ContentBlock, ContentPageType } from './types';

export interface ContentPageTemplate {
  key: string;
  label: string;
  slug: string;
  type: ContentPageType;
  locale: string;
  title: string;
  subtitle?: string | null;
  body: string;
  bodyFormat: BodyFormat;
  blocks: ContentBlock[];
  helperText: string;
}

export interface ContentPageTemplateDraft {
  slug: string;
  type: ContentPageType;
  locale: string;
  title: string;
  subtitle: string;
  body: string;
  bodyFormat: BodyFormat;
  blocksJson: string;
}

const pageTemplate = (
  template: Omit<ContentPageTemplate, 'key' | 'label' | 'helperText'> & {
    key: string;
    label: string;
    helperText: string;
  },
): ContentPageTemplate => template;

const privacyPolicyBody =
  '# Политика конфиденциальности\n\n' +
  '## 1. Общие положения\n\n' +
  'Настоящая политика описывает, как сервис OneBazaar обрабатывает персональные данные клиентов Telegram Mini App в соответствии с Федеральным законом от 27.07.2006 N 152-ФЗ "О персональных данных".\n\n' +
  'Оператор сервиса организует обработку персональных данных, определяет цели обработки, состав данных и действия, совершаемые с данными.\n\n' +
  '## 2. Какие данные обрабатываются\n\n' +
  'Мы можем обрабатывать имя, фамилию, отчество, дату рождения, номер телефона, email, Telegram ID, username, адреса доставки, сведения о заказах, согласиях и технические данные, необходимые для работы Mini App.\n\n' +
  'Обрабатываются только данные, необходимые для регистрации, оформления и исполнения заказов, доставки, оплаты, поддержки клиентов, соблюдения требований закона и защиты прав оператора.\n\n' +
  '## 3. Цели и основания обработки\n\n' +
  'Данные используются для создания и ведения профиля, идентификации клиента в Telegram Mini App, оформления корзины и заказов, доставки товаров, информирования о статусе заказа и обратной связи.\n\n' +
  'Обработка выполняется на основании согласия клиента, исполнения договора с клиентом, требований законодательства РФ и законных интересов оператора, если это не нарушает права и свободы клиента.\n\n' +
  '## 4. Передача и хранение данных\n\n' +
  'Данные могут передаваться сервисам, которые участвуют в исполнении заказа: платежным, доставочным, техническим и учетным системам, если это необходимо для работы сервиса.\n\n' +
  'Срок хранения определяется целями обработки, сроком действия согласия, требованиями законодательства РФ и необходимостью защиты прав оператора.\n\n' +
  '## 5. Права клиента\n\n' +
  'Клиент вправе получать сведения об обработке своих персональных данных, требовать уточнения, блокирования или удаления данных, отзывать согласие и закрыть профиль.\n\n' +
  'После закрытия профиля доступ к аккаунту прекращается, персональные данные удаляются или обезличиваются, а часть информации может храниться в течение срока, установленного законом.';

const dataConsentBody =
  '# Согласие на обработку данных\n\n' +
  '## 1. Согласие клиента\n\n' +
  'Нажимая кнопку "Войти" на странице регистрации, клиент свободно, своей волей и в своем интересе дает согласие на обработку персональных данных оператором сервиса OneBazaar.\n\n' +
  'Согласие действует до достижения целей обработки, закрытия профиля, отзыва согласия или истечения сроков хранения, установленных законодательством РФ.\n\n' +
  '## 2. Перечень персональных данных\n\n' +
  'Согласие распространяется на данные, которые клиент указывает при регистрации и использовании сервиса: имя, фамилию, отчество, дату рождения, телефон, email, Telegram ID, username, адреса доставки, сведения о заказах и согласиях.\n\n' +
  'Если клиент подтверждает передачу номера телефона через Telegram, номер может быть получен ботом и добавлен в профиль после обработки.\n\n' +
  '## 3. Действия с персональными данными\n\n' +
  'Оператор может выполнять сбор, запись, систематизацию, накопление, хранение, уточнение, использование, передачу, обезличивание, блокирование, удаление и уничтожение персональных данных.\n\n' +
  'Обработка может выполняться с использованием средств автоматизации и без их использования.\n\n' +
  '## 4. Цели обработки\n\n' +
  'Данные обрабатываются для регистрации профиля, оформления заказов, доставки, оплаты, отправки уведомлений по заказам, клиентской поддержки, исполнения требований законодательства РФ и обеспечения безопасности сервиса.\n\n' +
  'Отдельные необязательные согласия на акции и специальные предложения используются только для выбранных клиентом каналов связи.\n\n' +
  '## 5. Отзыв согласия\n\n' +
  'Клиент может отозвать согласие или закрыть профиль в разделе "Конфиденциальность и данные" в профиле клиента.\n\n' +
  'После отзыва согласия оператор прекращает обработку данных, кроме случаев, когда дальнейшая обработка допускается или требуется законодательством РФ.';

// NOTE: `label` and `helperText` below are i18next KEY strings (e.g.
// 'content:templates.shopOverview.label'), not literal display text — this is merchant-portal
// UI chrome for the template picker, so it follows the active language like everything else in
// this feature. Consumers must render `t(label)` / `t(helperText)`. Everything else in each
// template (`title`, `subtitle`, `body`, `blocks`) is genuine tenant-authored CMS *content* shown
// to shoppers on the storefront (a Russian-market shop, correctly authored in Russian) — left
// untouched, per plan-009's scope.
export const CONTENT_PAGE_TEMPLATES: ContentPageTemplate[] = [
  pageTemplate({
    key: 'shop-overview',
    label: 'content:templates.shopOverview.label',
    helperText: 'content:templates.shopOverview.helperText',
    slug: 'shop-overview',
    type: 'SHOP_INFO_SECTION',
    locale: 'ru',
    title: 'О магазине',
    subtitle: 'Цветы, которые помогают говорить о важном без лишних слов',
    body:
      '## Наша миссия\n\n' +
      'Собирать свежие букеты и доставлять их вовремя, чтобы каждый заказ превращался в знак внимания, заботы и благодарности.',
    bodyFormat: 'MARKDOWN',
    blocks: [],
  }),
  pageTemplate({
    key: 'payment',
    label: 'content:templates.payment.label',
    helperText: 'content:templates.payment.helperText',
    slug: 'payment',
    type: 'SHOP_INFO_SECTION',
    locale: 'ru',
    title: 'Оплата',
    subtitle: 'Способы оплаты и ограничения',
    body: '## Оплата\n\n' + 'Ниже перечислены доступные способы оплаты и правила их применения.',
    bodyFormat: 'MARKDOWN',
    blocks: [
      {
        blockType: 'PAYMENT_METHODS',
        position: 0,
        items: [
          { primary: 'Банковские карты РФ', secondary: 'Visa, Mastercard, МИР' },
          { primary: 'СБП', secondary: 'Оплата через Систему быстрых платежей' },
          { primary: 'Иностранные карты', secondary: 'Не принимаются' },
        ],
      },
    ],
  }),
  pageTemplate({
    key: 'delivery',
    label: 'content:templates.delivery.label',
    helperText: 'content:templates.delivery.helperText',
    slug: 'delivery',
    type: 'SHOP_INFO_SECTION',
    locale: 'ru',
    title: 'Доставка',
    subtitle: 'Способы и сроки доставки',
    body: '## Доставка\n\nОпишите сроки самовывоза, курьера и экспресс-доставки.',
    bodyFormat: 'MARKDOWN',
    blocks: [
      {
        blockType: 'DELIVERY_OPTIONS',
        position: 0,
        items: [
          { primary: 'Самовывоз', secondary: 'Готово в течение 2 часов' },
          { primary: 'Курьерская доставка', secondary: '1–2 дня · 299 ₽' },
          { primary: 'Экспресс-доставка', secondary: '2–4 часа · 699 ₽' },
        ],
      },
    ],
  }),
  pageTemplate({
    key: 'returns',
    label: 'content:templates.returns.label',
    helperText: 'content:templates.returns.helperText',
    slug: 'returns',
    type: 'SHOP_INFO_SECTION',
    locale: 'ru',
    title: 'Возврат товара',
    subtitle: 'Правила возврата и обращения',
    body: '## Возврат\n\nОпишите, как покупатель оформляет возврат или замену товара.',
    bodyFormat: 'MARKDOWN',
    blocks: [
      {
        blockType: 'RETURN_SECTIONS',
        position: 0,
        items: [
          {
            primary: 'Цветы и букеты',
            secondary: 'Возврат или замена возможны при ненадлежащем качестве в день доставки.',
          },
          {
            primary: 'Сопутствующие товары',
            secondary: 'Товары надлежащего качества можно вернуть в течение 7 дней.',
          },
          {
            primary: 'Возврат денег',
            secondary: 'Возврат выполняется на ту же карту, сроки зависят от банка.',
          },
        ],
      },
    ],
  }),
  pageTemplate({
    key: 'contacts',
    label: 'content:templates.contacts.label',
    helperText: 'content:templates.contacts.helperText',
    slug: 'contacts',
    type: 'SHOP_INFO_SECTION',
    locale: 'ru',
    title: 'Контакты',
    subtitle: 'Как связаться с магазином',
    body: '## Контакты\n\nУкажите часы работы, телефон, email и адреса.',
    bodyFormat: 'MARKDOWN',
    blocks: [
      {
        blockType: 'CONTACT_ROWS',
        position: 0,
        items: [
          { primary: 'Пн–Вс: 09:00 – 21:00', secondary: 'Поддержка 7 дней в неделю' },
          { primary: 'Телефон: +7 (800) 555-35-35', secondary: 'Основной контакт' },
          { primary: 'info@example.ru', secondary: 'Email для обращений' },
          { primary: 'Москва, ул. Примерная, 1', secondary: 'Фактический адрес' },
        ],
      },
    ],
  }),
  pageTemplate({
    key: 'legal-info',
    label: 'content:templates.legalInfo.label',
    helperText: 'content:templates.legalInfo.helperText',
    slug: 'legal-info',
    type: 'SHOP_INFO_SECTION',
    locale: 'ru',
    title: 'Правовая информация',
    subtitle: 'Краткие правовые примечания',
    body: '## Правовая информация\n\nДобавьте короткий свод правовых примечаний для ShopInfo.',
    bodyFormat: 'MARKDOWN',
    blocks: [
      {
        blockType: 'FAQ',
        position: 0,
        items: [
          { primary: 'Cookies', secondary: 'Используются для авторизации и работы корзины.' },
          { primary: 'Яндекс Метрика', secondary: 'Используется для аналитики посещаемости.' },
        ],
      },
    ],
  }),
  pageTemplate({
    key: 'privacy-policy',
    label: 'content:templates.privacyPolicy.label',
    helperText: 'content:templates.privacyPolicy.helperText',
    slug: 'privacy-policy',
    type: 'LEGAL_DOCUMENT',
    locale: 'ru',
    title: 'Политика конфиденциальности',
    subtitle: 'Информация о порядке обработки и защиты персональных данных клиентов.',
    body: privacyPolicyBody,
    bodyFormat: 'MARKDOWN',
    blocks: [],
  }),
  pageTemplate({
    key: 'data-consent',
    label: 'content:templates.dataConsent.label',
    helperText: 'content:templates.dataConsent.helperText',
    slug: 'data-consent',
    type: 'LEGAL_DOCUMENT',
    locale: 'ru',
    title: 'Согласие на обработку данных',
    subtitle: 'Условия согласия клиента на обработку персональных данных в Telegram Mini App.',
    body: dataConsentBody,
    bodyFormat: 'MARKDOWN',
    blocks: [],
  }),
];

export function getContentPageTemplate(key: string): ContentPageTemplate | undefined {
  return CONTENT_PAGE_TEMPLATES.find((template) => template.key === key);
}

export function templateDraft(template: ContentPageTemplate): ContentPageTemplateDraft {
  return {
    slug: template.slug,
    type: template.type,
    locale: template.locale,
    title: template.title,
    subtitle: template.subtitle ?? '',
    body: template.body,
    bodyFormat: template.bodyFormat,
    blocksJson: JSON.stringify(template.blocks, null, 2),
  };
}
